import { useCallback, useMemo } from "react";

import { useQuery } from "@apollo/client";
import { USER_ARPS } from "constants/settings.constants";

import { FOLDERS_LIGHT } from "api/userArps";

import { getForecastFolderDisplayName } from "components/groupBy/helpers/qualifyForecastProperty";
import { useSelectedProject } from "components/project/projects/hooks";

/** The two fields of a folder this resolver reads. FOLDERS_LIGHT returns more. */
interface ForecastFolderName {
  folderId: string;
  name: string;
}

export interface ForecastFolderNameResolver {
  /** The name to show for a stored source, or undefined when it cannot be named. */
  resolveForecastFolderName: (forecastFolder: Maybe<string>) => Maybe<string>;
  /** True only while the project's folder list is in flight. */
  isLoadingFolders: boolean;
}

/**
 * Returns a resolver from a stored forecast source to the name to show for it.
 *
 * The four built-in sources answer from the static map, synchronously, so they never wait on a
 * query. Anything else is a forecast folder belonging to the selected project, and only the
 * project's folder list can name it — that list is the whole reason this hook exists.
 *
 * The resolver returns undefined for a source it cannot name, and isLoadingFolders says which
 * of the two reasons that is. In flight, no folder has a name yet. Once the list has answered,
 * undefined means the folder is not in it at all: deleted, access revoked, or the tile was
 * saved in another project. The caller has to tell those apart because it commits the name it
 * shows, not just displays it — the raw id is the honest answer for a folder that is really
 * gone, but committing it during the window before the list arrives pins a bare id onto the
 * axis that nothing later re-resolves. Returning an empty name instead would be a lie in
 * either state: an empty forecast source reads as "no forecast" everywhere else.
 *
 * Reads the same query as ForecastSourceSelect with the same variables, so the two share one
 * Apollo cache entry and the second consumer costs no request. A cache hit answers with
 * loading already false, so a caller that waits on this only waits the first time.
 */
export const useForecastFolderNameResolver = (): ForecastFolderNameResolver => {
  const { selectedProject } = useSelectedProject();
  const projectId = selectedProject?.projectId;

  const { data, loading } = useQuery(FOLDERS_LIGHT, {
    variables: {
      req: {
        projectId: projectId ?? "",
        type: USER_ARPS
      }
    },
    skip: !projectId
  });

  const nameByFolderId = useMemo(() => {
    const folders: ForecastFolderName[] = data?.folders ?? [];
    return folders.reduce<Record<string, string>>((names, folder) => {
      if (folder?.folderId && folder?.name) {
        names[folder.folderId] = folder.name;
      }
      return names;
    }, {});
  }, [data]);

  const resolveForecastFolderName = useCallback(
    (forecastFolder: Maybe<string>): Maybe<string> =>
      getForecastFolderDisplayName(forecastFolder) ??
      (forecastFolder ? nameByFolderId[forecastFolder] : undefined),
    [nameByFolderId]
  );

  // A skipped query never loads, so a user with no selected project falls straight through to
  // the id fallback rather than waiting on an answer that is not coming.
  return { resolveForecastFolderName, isLoadingFolders: loading };
};
